import { useCallback, useMemo, useState } from 'react'
import { Plus, SlidersHorizontal, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchAutoRules,
  createAutoRule,
  deleteAutoRule,
  BAN_STR as S,
  type AutoRuleRow,
  type RestrictionAction,
} from './strikesApi'

const ACTIONS: { value: RestrictionAction; label: string }[] = [
  { value: 'warn', label: S.actWarn },
  { value: 'strike', label: S.actStrike },
  { value: 'mute', label: S.actMute },
  { value: 'ban', label: S.actBan },
]

const inputCls =
  'rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong'

/**
 * A8.04 — Avtomatik cheklov qoidalari. JONLI `/admin/ban-rules` (000245):
 * inline qo'shish-forma (trigger→threshold→action) + o'chirish row-action,
 * DataTable har mutatsiyadan keyin reloadKey orqali qayta yuklanadi.
 */
export function AutoRulesScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [trigger, setTrigger] = useState('')
  const [threshold, setThreshold] = useState('3')
  const [action, setAction] = useState<RestrictionAction>('strike')
  const [durationDays, setDurationDays] = useState('0')
  const [enabled, setEnabled] = useState(true)
  const [busy, setBusy] = useState(false)

  const add = async () => {
    if (!trigger.trim()) return
    setBusy(true)
    try {
      await createAutoRule({
        trigger: trigger.trim(),
        threshold: Math.max(1, Number(threshold) || 1),
        action,
        duration_days: Math.max(0, Number(durationDays) || 0),
        enabled,
      })
      toast(S.addOk, { tone: 'success' })
      setTrigger('')
      setReloadKey((k) => k + 1)
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const columns = useMemo<AdminColumn<AutoRuleRow>[]>(
    () => [
      { key: 'trigger', header: S.trigger, type: 'text', minWidth: 200 },
      { key: 'threshold', header: S.threshold, type: 'number', width: 110, align: 'right' },
      {
        key: 'action',
        header: S.action,
        type: 'enum',
        width: 140,
        options: [
          { value: 'warn', label: S.actWarn, status: 'review' },
          { value: 'strike', label: S.actStrike, status: 'pending' },
          { value: 'mute', label: S.actMute, status: 'review' },
          { value: 'ban', label: S.actBan, status: 'banned' },
        ],
      },
      { key: 'duration_days', header: S.duration, type: 'number', width: 100, align: 'right' },
      { key: 'enabled', header: S.enabled, type: 'bool', width: 100 },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<AutoRuleRow>[]>(
    () => [
      {
        id: 'delete',
        label: S.remove,
        icon: <Trash2 size={14} />,
        danger: true,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: async (r) => {
          try {
            await deleteAutoRule(r.id)
            toast(S.removed, { tone: 'success' })
            setReloadKey((k) => k + 1)
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const fetcher = useCallback(fetchAutoRules, [])
  return (
    <>
      <PageHeader title={S.autoRules} screenId="A8.04" description={S.autoRulesNote} />
      <div className="flex flex-wrap items-end gap-2 p-4 pb-0">
        <input
          value={trigger}
          onChange={(e) => setTrigger(e.target.value)}
          placeholder={S.trigger}
          className={`${inputCls} min-w-[200px] flex-1`}
        />
        <input
          type="number"
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          placeholder={S.threshold}
          className={`${inputCls} w-24`}
        />
        <select
          value={action}
          onChange={(e) => setAction(e.target.value as RestrictionAction)}
          className={inputCls}
        >
          {ACTIONS.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
        <input
          type="number"
          value={durationDays}
          onChange={(e) => setDurationDays(e.target.value)}
          placeholder={S.duration}
          className={`${inputCls} w-24`}
        />
        <label className="flex items-center gap-1.5 px-1 text-sm text-secondary">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          {S.enabled}
        </label>
        <button
          type="button"
          onClick={add}
          disabled={busy || !trigger.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          <Plus size={14} />
          {S.add}
        </button>
      </div>
      <DataTable<AutoRuleRow>
        key={reloadKey}
        screenId="A8.04"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <SlidersHorizontal size={28} strokeWidth={1.5} />, title: S.autoRules }}
      />
    </>
  )
}
